<script setup>
import { computed } from 'vue';
import { MESSAGE_STATUS } from '../../constants';
import { useMessageContext } from '../../provider.js';

const { contentAttributes, status, sender } = useMessageContext();

const hasError = computed(() => {
  return status.value === MESSAGE_STATUS.FAILED;
});

const fromEmail = computed(() => {
  return contentAttributes.value?.email?.from ?? [];
});

const toEmail = computed(() => {
  const { toEmails, email } = contentAttributes.value;
  return email?.to ?? toEmails ?? [];
});

const ccEmail = computed(() => {
  return (
    contentAttributes.value?.ccEmails ??
    contentAttributes.value?.email?.cc ??
    []
  );
});

const senderName = computed(() => {
  const fromEmailAddress = fromEmail.value[0] ?? '';
  const senderEmail = sender.value.email ?? '';

  if (!fromEmailAddress && !senderEmail) return null;

  // if the sender of the conversation and the sender of this particular
  // email are the same, only then we return the sender name
  if (fromEmailAddress === senderEmail) {
    return sender.value.name;
  }

  return null;
});

const bccEmail = computed(() => {
  return (
    contentAttributes.value?.bccEmails ??
    contentAttributes.value?.email?.bcc ??
    []
  );
});

const subject = computed(() => {
  return contentAttributes.value?.email?.subject ?? '';
});

const showMeta = computed(() => {
  return (
    fromEmail.value[0] ||
    toEmail.value.length ||
    ccEmail.value.length ||
    bccEmail.value.length ||
    subject.value
  );
});
</script>

<template>
  <!-- The kit's EmailMessage header (R2-T-P4 = A): the subject is the title,
       who wrote to whom is a caption list. Addresses wrap instead of being cut,
       because the person needs the whole list to reply to everyone. -->
  <section v-show="showMeta" class="grid gap-1 rtl:pl-9 ltr:pr-9 break-words">
    <template v-if="showMeta">
      <h4
        v-if="subject"
        class="type-polaris-card-title"
        :class="
          hasError ? 'text-polaris-destructive' : 'text-polaris-foreground'
        "
      >
        {{ subject }}
      </h4>
      <dl
        class="type-polaris-caption grid grid-cols-[auto_1fr] gap-x-2 text-polaris-muted-foreground"
      >
        <template v-if="fromEmail[0]">
          <dt>{{ $t('EMAIL_HEADER.FROM') }}</dt>
          <dd
            class="min-w-0"
            :class="
              hasError ? 'text-polaris-destructive' : 'text-polaris-foreground'
            "
          >
            <template v-if="senderName">
              <span>{{ senderName }}</span>
              &lt;{{ fromEmail[0] }}&gt;
            </template>
            <template v-else>
              {{ fromEmail[0] }}
            </template>
          </dd>
        </template>
        <template v-if="toEmail.length">
          <dt>{{ $t('EMAIL_HEADER.TO') }}</dt>
          <dd class="min-w-0 text-polaris-foreground">
            {{ toEmail.join(', ') }}
          </dd>
        </template>
        <template v-if="ccEmail.length">
          <dt>{{ $t('EMAIL_HEADER.CC') }}</dt>
          <dd class="min-w-0 text-polaris-foreground">
            {{ ccEmail.join(', ') }}
          </dd>
        </template>
        <template v-if="bccEmail.length">
          <dt>{{ $t('EMAIL_HEADER.BCC') }}</dt>
          <dd class="min-w-0 text-polaris-foreground">
            {{ bccEmail.join(', ') }}
          </dd>
        </template>
      </dl>
    </template>
  </section>
</template>
